/**
 * useAdvancedFiltersIntegration Hook
 *
 * Complete integration of Advanced Filters functionality for MetadataTable consumers.
 * Combines useAdvancedFilters + useSorting + drawer state management into one reusable hook.
 *
 * This hook provides everything needed to add Advanced Filters to any table:
 * - Filter state management
 * - Sort state management
 * - Drawer open/close state
 * - Prop bundles for MetadataTableToolbar and AdvancedFiltersDrawer
 * - Active filter/sort counts for badges
 *
 * @example Basic usage
 * ```tsx
 * const { toolbarProps, drawerProps, apiFilters, sortFields } = useAdvancedFiltersIntegration({
 *   model: 'offering',
 *   defaultFilters,
 *   defaultSort,
 *   fields,
 * })
 *
 * return (
 *   <>
 *     <MetadataTableToolbar {...toolbarProps} />
 *     <MetadataTable data={data} />
 *     <AdvancedFiltersDrawer {...drawerProps} />
 *   </>
 * )
 * ```
 */

import { useMemo, useCallback } from 'react'
import { useAdvancedFilters } from '../AdvancedFilters/useAdvancedFilters'
import { useSorting } from '../AdvancedFilters/hooks/useSorting'
import type { DefaultFilter, DefaultSort, FieldMetadata } from '@/services/metadataService'
import type { Filter } from '@/types/api.types'
import type { FilterRow, SortField } from '../AdvancedFilters/advancedFiltersTypes'

export interface UseAdvancedFiltersIntegrationOptions {
  /** Model name for metadata lookup */
  model: string

  /** Default filters from metadata (initialize filters from YAML) */
  defaultFilters?: DefaultFilter[]

  /** Default sort from metadata (initialize sorts from YAML) */
  defaultSort?: DefaultSort[]

  /** Available fields for filter/sort (from useMetadataColumns) */
  fields: Record<string, FieldMetadata>

  /** Callback when filters change (for page reset, etc.) */
  onFiltersChange?: () => void

  /** Callback when sorts change */
  onSortsChange?: () => void

  /** Base filters to apply when fetching distinct values for autocomplete */
  baseFilters?: Filter[]

  /** Whether the table has lifecycle states (to filter autocomplete values) */
  hasLifecycle?: boolean
}

export interface UseAdvancedFiltersIntegrationResult {
  // === Filter State ===
  /** Current advanced filter rows */
  filterRows: FilterRow[]

  /** Filters formatted for API (includes conversion of BETWEEN, etc.) */
  apiFilters: Filter[]

  /** Number of active filters (for badge) */
  activeFilterCount: number

  /** Set filter rows directly */
  setFilterRows: (rows: FilterRow[]) => void

  /** Clear all filters */
  clearFilters: () => void

  /** Reset filters to metadata defaults */
  resetFiltersToDefaults: () => void

  /** Whether metadata defaults exist for filters */
  hasDefaultFilters: boolean

  // === Sort State ===
  /** Current sort fields (draft state for drawer editing) */
  sortFields: SortField[]

  /** Applied sort fields for API consumption (only updated on Apply) */
  appliedSortFields: Array<{ key: string; direction: 'asc' | 'desc' }>

  /** Number of active sorts (for badge) */
  activeSortCount: number

  /** Set sort fields directly */
  setSortFields: (fields: SortField[]) => void

  /**
   * Set applied sort fields directly, bypassing draft state.
   * Used for column header clicks where we want immediate effect.
   * Also syncs draft state to match so AF drawer shows current state.
   */
  setAppliedSortFieldsDirectly: (fields: Array<{ key: string; direction: 'asc' | 'desc' }>) => void

  /** Clear all sorts */
  clearSorts: () => void

  /** Reset sorts to metadata defaults */
  resetSortsToDefaults: () => void

  /** Whether metadata defaults exist for sorts */
  hasDefaultSort: boolean

  // === Drawer State ===
  /** Whether drawer is open */
  isDrawerOpen: boolean

  /** Open drawer */
  openDrawer: () => void

  /** Close drawer */
  closeDrawer: () => void

  // === Props Bundles (for easy wiring) ===
  /**
   * Props bundle for MetadataTableToolbar.
   * Spread these onto toolbar component.
   */
  toolbarProps: {
    advancedFilterCount: number
    advancedSortCount: number
    onOpenAdvancedFilters: () => void
  }

  /**
   * Props bundle for AdvancedFiltersDrawer.
   * Spread these onto drawer component.
   */
  drawerProps: {
    model: string
    open: boolean
    onClose: () => void
    fields: Record<string, FieldMetadata>
    filters: FilterRow[]
    onFiltersChange: (rows: FilterRow[]) => void
    sortFields: SortField[]
    onSortFieldsChange: (fields: SortField[]) => void
    onApply: () => void
    defaultFilters?: DefaultFilter[]
    defaultSortFields?: DefaultSort[]
    onResetFiltersToDefaults: () => void
    onResetSortToDefaults: () => void
  }
}

/**
 * Complete Advanced Filters integration hook.
 *
 * Combines useAdvancedFilters + useSorting + drawer state into one reusable hook.
 * Provides prop bundles for easy wiring to MetadataTableToolbar and AdvancedFiltersDrawer.
 *
 * @param options - Configuration options
 * @returns Advanced Filters state and prop bundles
 */
export function useAdvancedFiltersIntegration(
  options: UseAdvancedFiltersIntegrationOptions
): UseAdvancedFiltersIntegrationResult {
  const {
    model,
    defaultFilters = [],
    defaultSort = [],
    fields,
    onFiltersChange,
    onSortsChange,
    baseFilters,
    hasLifecycle,
  } = options

  // Filter state management
  const {
    filters: filterRows,
    setFilters: setFilterRows,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    apiFilters,
    filterCount: activeFilterCount,
    applyFilters,
    revertFiltersToApplied,
    clearFilters: clearFiltersBase,
    resetFiltersToDefaults,
    hasDefaultFilters,
  } = useAdvancedFilters({ defaultFilters })

  // Sort state management
  const {
    sortFields,
    setSortFields,
    clearSort,
    applySort,
    revertSortToApplied,
    appliedApiSortFields,
    setAppliedSortFieldsDirectly,
    resetSortToDefaults,
    hasDefaultSort,
  } = useSorting({ defaultSortFields: defaultSort })

  // Use the memoized applied sort fields directly (no useMemo wrapper needed)
  // This prevents creating new array references when the toApiFormat function changes
  const appliedSortFields = appliedApiSortFields

  // Combine counts for badges
  const activeSortCount = sortFields.filter((s) => s.key).length

  // Clear filters with callback
  const clearFilters = () => {
    clearFiltersBase()
    onFiltersChange?.()
  }

  // Clear sorts with callback
  const clearSorts = () => {
    clearSort()
    onSortsChange?.()
  }

  // Apply filters/sorts and close drawer
  const handleApply = () => {
    applyFilters()
    applySort()  // Also apply sort
    closeDrawer()
    onFiltersChange?.()
    onSortsChange?.()
  }

  // Cancel - revert draft state to applied state and close drawer
  const handleCancel = useCallback(() => {
    revertFiltersToApplied()
    revertSortToApplied()
    closeDrawer()
  }, [revertFiltersToApplied, revertSortToApplied, closeDrawer])

  // Props bundle for MetadataTableToolbar
  const toolbarProps = useMemo(
    () => ({
      advancedFilterCount: activeFilterCount,
      advancedSortCount: activeSortCount,
      onOpenAdvancedFilters: openDrawer,
    }),
    [activeFilterCount, activeSortCount, openDrawer]
  )

  // Props bundle for AdvancedFiltersDrawer
  const drawerProps = useMemo(
    () => ({
      model,
      open: isDrawerOpen,
      onClose: handleCancel,  // Cancel reverts draft to applied state
      fields,
      filters: filterRows,
      onFiltersChange: setFilterRows,
      sortFields,
      onSortFieldsChange: setSortFields,
      onApply: handleApply,
      defaultFilters,
      defaultSortFields: defaultSort,
      onResetFiltersToDefaults: resetFiltersToDefaults,
      onResetSortToDefaults: resetSortToDefaults,
      baseFilters,
      hasLifecycle,
    }),
    [
      model,
      isDrawerOpen,
      handleCancel,
      fields,
      filterRows,
      setFilterRows,
      sortFields,
      setSortFields,
      defaultFilters,
      defaultSort,
      resetFiltersToDefaults,
      resetSortToDefaults,
      baseFilters,
      hasLifecycle,
    ]
  )

  return {
    // Filter state
    filterRows,
    apiFilters,
    activeFilterCount,
    setFilterRows,
    clearFilters,
    resetFiltersToDefaults,
    hasDefaultFilters,

    // Sort state
    sortFields,
    appliedSortFields,
    activeSortCount,
    setSortFields,
    setAppliedSortFieldsDirectly,
    clearSorts,
    resetSortsToDefaults: resetSortToDefaults,
    hasDefaultSort,

    // Drawer state
    isDrawerOpen,
    openDrawer,
    closeDrawer,

    // Props bundles
    toolbarProps,
    drawerProps,
  }
}
